import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export type TrialState = 'active_trial' | 'trial_expired' | 'paid_expired' | 'paid_active' | 'no_subscription';

interface TrialStatus {
  state: TrialState;
  daysRemaining: number | null;
  daysUntilDeletion: number | null;
  isTrialExpired: boolean;
  canAccessDashboard: (dashboardPath: string) => boolean;
  loading: boolean;
}

export const useTrialStatus = (): TrialStatus => {
  const { user, loading: authLoading } = useAuth();
  const [trialStatus, setTrialStatus] = useState<TrialStatus>({
    state: 'no_subscription',
    daysRemaining: null,
    daysUntilDeletion: null,
    isTrialExpired: false,
    canAccessDashboard: () => false,
    loading: true,
  });

  useEffect(() => {
    // If auth is still loading, keep trial status loading
    if (authLoading) {
      setTrialStatus(prev => ({ ...prev, loading: true }));
      return;
    }

    const checkTrialStatus = async () => {
      if (!user) {
        setTrialStatus({
          state: 'no_subscription',
          daysRemaining: null,
          daysUntilDeletion: null,
          isTrialExpired: false,
          canAccessDashboard: () => false,
          loading: false,
        });
        return;
      }

      // Get user's PTA first (latest row — tolerant of stray duplicates)
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('pta_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (memberError || !memberData?.pta_id) {
        // New user with no PTA membership - allow onboarding flow
        setTrialStatus({
          state: 'no_subscription',
          daysRemaining: null,
          daysUntilDeletion: null,
          isTrialExpired: false,
          canAccessDashboard: (path: string) => {
            const allowedPaths = ['/onboarding', '/auth', '/', '/pricing', '/settings'];
            return allowedPaths.some(p => path.startsWith(p));
          },
          loading: false,
        });
        return;
      }

      // Fetch PTA's subscription details including trial fields
      const { data: subData, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('pta_id', memberData.pta_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !subData) {
        // Has PTA but no subscription - treat as trial expired
        setTrialStatus({
          state: 'trial_expired',
          daysRemaining: null,
          daysUntilDeletion: null,
          isTrialExpired: true,
          canAccessDashboard: (path: string) => {
            const allowedPaths = ['/pta-admin', '/auth', '/trial-expired', '/', '/pricing', '/settings'];
            const blockedDashboards = [
              '/dashboard/chair', '/dashboard/treasurer', '/dashboard/secretary',
              '/dashboard/trustee', '/dashboard/assets', '/dashboard/catering',
              '/dashboard/events', '/dashboard/communications', '/dashboard/community',
              '/dashboard/school-liaison', '/dashboard/shop', '/dashboard'
            ];
            if (blockedDashboards.some(p => path.startsWith(p))) return false;
            return allowedPaths.some(p => path.startsWith(p));
          },
          loading: false,
        });
        return;
      }

      const status = subData.status;
      const trialEndsAt = subData.trial_ends_at ? new Date(subData.trial_ends_at) : null;
      const scheduledDeletionAt = subData.scheduled_deletion_at ? new Date(subData.scheduled_deletion_at) : null;
      const now = new Date();

      let state: TrialState = 'no_subscription';
      let daysRemaining: number | null = null;
      let daysUntilDeletion: number | null = null;
      let isTrialExpired = false;

      if (status === 'trial' && trialEndsAt) {
        state = 'active_trial';
        const diffTime = trialEndsAt.getTime() - now.getTime();
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        isTrialExpired = daysRemaining <= 0;
      } else if (status === 'trial_expired' || status === 'pending_deletion') {
        state = 'trial_expired';
        isTrialExpired = true;
      } else if (status === 'expired') {
        state = 'paid_expired';
        isTrialExpired = true;
        if (scheduledDeletionAt) {
          const diffTime = scheduledDeletionAt.getTime() - now.getTime();
          daysUntilDeletion = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
      } else if (status === 'active') {
        state = 'paid_active';
        isTrialExpired = false;
      }

      const canAccessDashboard = (dashboardPath: string): boolean => {
        // If paid active or active trial, allow all access
        if (state === 'paid_active' || state === 'active_trial') {
          return true;
        }

        // If trial expired, only allow PTA admin, auth, trial-expired, and public pages
        if (state === 'trial_expired' || state === 'paid_expired') {
          const allowedPaths = ['/pta-admin', '/auth', '/trial-expired', '/', '/pricing', '/settings'];
          
          // Block all dashboard pages when trial expired
          const blockedDashboards = [
            '/dashboard/chair',
            '/dashboard/treasurer', 
            '/dashboard/secretary',
            '/dashboard/trustee',
            '/dashboard/assets',
            '/dashboard/catering',
            '/dashboard/events',
            '/dashboard/communications',
            '/dashboard/community',
            '/dashboard/school-liaison',
            '/dashboard/shop',
            '/dashboard'
          ];
          
          if (blockedDashboards.some(path => dashboardPath.startsWith(path))) {
            return false;
          }
          
          return allowedPaths.some(path => dashboardPath.startsWith(path));
        }

        return false;
      };

      setTrialStatus({
        state,
        daysRemaining,
        daysUntilDeletion,
        isTrialExpired,
        canAccessDashboard,
        loading: false,
      });
    };

    checkTrialStatus();
  }, [user, authLoading]);

  return trialStatus;
};
